import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageHeader } from '@/components/layout/page-header'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { ConsignmentList } from '@/app/(dashboard)/consignments/_components/ConsignmentList'
import { api } from '@/lib/api'
import { useAsyncData } from '@/src/hooks/useAsyncData'
import { LoadingState } from '@/src/components/LoadingState'
import { ErrorState } from '@/src/components/ErrorState'
import { Pagination } from '@/src/components/Pagination'
import { ConsignmentQuickEdit, QuickEditFailure } from '@/src/components/QuickEdit'
import { personName, useHelmPeople } from '@/src/hooks/useHelmPeople'
import { useOptimisticRows } from '@/src/hooks/useOptimisticRows'
import { useRowSelection, useSelectionKeys } from '@/src/hooks/useRowSelection'
import { ConsignmentBatchActions, SelectModeButton } from '@/src/components/BatchActions'
import { cn } from '@/lib/utils'
import type { Consignment } from '@/lib/api/types'

const NO_CONSIGNMENTS: Consignment[] = []

export function ConsignmentsPage() {
  const [page, setPage] = useState(1)
  // The search lives in the URL, so coming back from a consignment restores it.
  const [searchParams, setSearchParams] = useSearchParams()
  const search = searchParams.get('q') ?? ''
  const [draft, setDraft] = useState(search)

  useEffect(() => {
    if (draft.trim() === search) return
    const timer = window.setTimeout(() => {
      setPage(1)
      setSearchParams(draft.trim() ? { q: draft.trim() } : {}, { replace: true })
    }, 300)
    return () => window.clearTimeout(timer)
  }, [draft, search, setSearchParams])

  const {
    data: result,
    error,
    loading,
    reload,
  } = useAsyncData(() => api.consignments.listPage(page, 25, search), [page, search])
  // Quick edits show on the row before the API answers.
  const edits = useOptimisticRows(result?.items ?? NO_CONSIGNMENTS)
  const people = useHelmPeople()
  const meId = people.data?.meId
  const canEdit = Boolean(people.data?.canEdit)
  // Picked rows, for changing many at once; kept across pages and searches.
  const selection = useRowSelection<Consignment>()
  useSelectionKeys(selection, edits.rows, canEdit)

  // Only the first load replaces the page; later loads keep the search box mounted.
  if (loading && !result) return <LoadingState label="Loading consignments..." />
  if (error && !result) return <ErrorState error={error} onRetry={reload} />

  return (
    // Room under the last row for the batch bar or, on a phone, Beacon's button.
    <div className={cn('space-y-6', selection.count > 0 ? 'pb-40 md:pb-20' : 'pb-20 md:pb-0')}>
      <PageHeader
        title="Consignments"
        description="Manage and track import consignments"
        actions={
          <>
            {canEdit && <SelectModeButton selection={selection} />}
            <Link href="/consignments/new">
              <Button variant="brand">+ New Consignment</Button>
            </Link>
          </>
        }
      />

      {error && (
        <p role="alert" className="text-sm text-destructive">
          Search failed: {error.message}
        </p>
      )}

      <QuickEditFailure edits={edits} />

      <ConsignmentList
        consignments={edits.rows}
        search={draft}
        onSearchChange={setDraft}
        searching={loading || draft.trim() !== search}
        ownerLabel={(ownerUserId) =>
          ownerUserId === meId ? 'You' : personName(people.data?.users.get(ownerUserId))
        }
        rowActions={
          canEdit
            ? (consignment) => <ConsignmentQuickEdit consignment={consignment} edits={edits} />
            : undefined
        }
        selection={canEdit ? selection : undefined}
        footer={
          result && (
            <Pagination
              className="px-4 py-3 md:px-6"
              page={result.page}
              limit={result.limit}
              total={result.total}
              totalPages={result.totalPages}
              onPageChange={setPage}
              noun="consignments"
            />
          )
        }
      />

      {canEdit && (
        <ConsignmentBatchActions
          selection={selection}
          edits={edits}
          people={people.data?.users ?? new Map()}
          meId={meId ?? null}
        />
      )}
    </div>
  )
}
